import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ProviderContext } from "../../contexts/provider.context";
import { getAllCandidate, getInterviewQuestion, getInterviewQuestionByUserID } from "../../services/api.service";
import { QuestionsSummaryModel } from "../../model/questions-summary.model";
import { Candidate } from "../../model/candidate.model";
import "./interviewer-list.component.scss";

type Row = Candidate & { job_name?: string };
type SortKey = "name" | "email" | "phone" | "job" | "status";
const INTERVIEW_SET_FETCH_MAX_PAGES = 1000;

const InterviewerListComponent = () => {
    const { profileModel }: any = useContext(ProviderContext);
    const [rows, setRows] = useState<Row[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 20;
    const [sort, setSort] = useState<{ key: SortKey; direction: "asc" | "desc" }>({ key: "name", direction: "asc" });

    const toggleSort = (key: SortKey) => {
        setSort((prev) => prev.key === key ? { key, direction: prev.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" });
    };

    const fetchInterviewSets = useCallback(async (): Promise<QuestionsSummaryModel[]> => {
        if (!profileModel?.roleName) {
            return [];
        }
        const out: QuestionsSummaryModel[] = [];
        let pageNum = 1;
        while (pageNum <= INTERVIEW_SET_FETCH_MAX_PAGES) {
            const res = profileModel.roleName === "ROLE_SUPER_ADMIN"
                ? await getInterviewQuestion(pageNum, 50)
                : await getInterviewQuestionByUserID(profileModel.uuid, pageNum, 50);
            if (!res.data?.is_success) break;
            const chunk = (res.data.data || []) as QuestionsSummaryModel[];
            out.push(...chunk);
            if (chunk.length === 0 || !res.data.pagination?.has_next) break;
            pageNum += 1;
        }
        // กันกรณี API ส่งข้อมูลซ้ำข้ามหน้า
        return out.filter(
            (set, index, arr) =>
                arr.findIndex((item) => item.job_question_id === set.job_question_id) === index,
        );
    }, [profileModel]);

    useEffect(() => {
        if (!profileModel?.roleName) {
            return;
        }
        let active = true;
        const run = async () => {
            setLoading(true);
            try {
                const interviewSets = await fetchInterviewSets();
                const allRows: Row[] = [];
                for (const set of interviewSets) {
                    const cRes = await getAllCandidate(set.job_question_id);
                    if (cRes?.data?.is_success && Array.isArray(cRes.data.data)) {
                        for (const c of cRes.data.data as Candidate[]) {
                            if (String(c.status || "").toUpperCase() === "DELETED") continue;
                            allRows.push({ ...c, job_name: set.job_position?.name || set.job_position?.job_title || "-" });
                        }
                    }
                }
                if (active) setRows(allRows);
            } finally {
                if (active) setLoading(false);
            }
        };
        run();
        return () => { active = false; };
    }, [fetchInterviewSets]);

    const filteredSorted = useMemo(() => {
        const s = search.trim().toLowerCase();
        const filtered = !s ? rows : rows.filter((r) =>
            `${r.firstname || ""} ${r.lastname || ""}`.toLowerCase().includes(s) ||
            String(r.email || "").toLowerCase().includes(s) ||
            String(r.phone || "").toLowerCase().includes(s) ||
            String(r.job_name || "").toLowerCase().includes(s)
        );
        return [...filtered].sort((a, b) => {
            const getV = (r: Row) => {
                if (sort.key === "name") return `${r.firstname || ""} ${r.lastname || ""}`.toLowerCase();
                if (sort.key === "email") return String(r.email || "").toLowerCase();
                if (sort.key === "phone") return String(r.phone || "").toLowerCase();
                if (sort.key === "job") return String(r.job_name || "").toLowerCase();
                return String(r.interview_status || "").toLowerCase();
            };
            const av = getV(a);
            const bv = getV(b);
            if (av < bv) return sort.direction === "asc" ? -1 : 1;
            if (av > bv) return sort.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [rows, search, sort]);

    const totalPages = Math.max(1, Math.ceil(filteredSorted.length / pageSize));
    const currentRows = filteredSorted.slice((page - 1) * pageSize, page * pageSize);

    useEffect(() => { setPage(1); }, [search, sort]);

    if (!profileModel?.roleName) {
        return (
            <div className="interviewer-list-page border-std">
                <div className="ilp-loading">กำลังเตรียมข้อมูลผู้ใช้งาน...</div>
            </div>
        );
    }

    return (
        <div className="interviewer-list-page border-std">
            <div className="ilp-header">
                <h2>ผู้สัมภาษณ์ทั้งหมด</h2>
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="ค้นหาชื่อ / อีเมล / เบอร์ / ตำแหน่ง"
                />
            </div>
            {loading ? (
                <div className="ilp-loading">กำลังโหลดข้อมูล...</div>
            ) : (
                <>
                    <table className="ilp-table">
                        <thead>
                            <tr>
                                <th onClick={() => toggleSort("name")}>ชื่อ-นามสกุล</th>
                                <th onClick={() => toggleSort("email")}>อีเมล</th>
                                <th onClick={() => toggleSort("phone")}>เบอร์โทร</th>
                                <th onClick={() => toggleSort("job")}>ชุดคำถาม/ตำแหน่ง</th>
                                <th onClick={() => toggleSort("status")}>สถานะ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentRows.map((r, i) => (
                                <tr key={`${r.candidate_id || i}-${r.email || ""}`}>
                                    <td>{`${r.firstname || ""} ${r.lastname || ""}`.trim() || "-"}</td>
                                    <td>{r.email || "-"}</td>
                                    <td>{r.phone || "-"}</td>
                                    <td>{r.job_name || "-"}</td>
                                    <td>{r.interview_status || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="ilp-pagination">
                        <span>{filteredSorted.length} รายการ · หน้า {page}/{totalPages}</span>
                        <div>
                            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>ก่อนหน้า</button>
                            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>ถัดไป</button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default InterviewerListComponent;
